import type {
  BooleanFlag,
  GeneralID,
  LinkTarget,
  MapLocation,
  MediaType,
  ModalSize,
  ModalSizeType,
  OpenStyle,
} from './baseTypes';

export enum FeatureType {
  link = 0,
  mobile = 1,
  map = 2,
  media = 3,
  text = 4,
  article = 5,
  audio = 6,
  video = 7,
  seqFrame = 8,
  scene = 9,
  noFunc = 10,
  event = 11,
  miniProgram = 12,
  doc = 13,
  model3d = 14,
  prevScene = 15,
  nextScene = 16,
}

export interface CFLink {
  code: FeatureType.link;
  title: string;
  linkUrl: string;
  openMethod: LinkTarget; // 1
  openStyle?: OpenStyle;
  turnOffBgMusicWhenLayerOpen: BooleanFlag; // 弹层打开时关闭背景音乐, 默认0
  turnOffBgVoiceWhenLayerOpen: BooleanFlag; // 弹层打开时关闭背景讲解, 默认0
  modalSizeType?: ModalSizeType;
  size?: ModalSize;
}

export interface CFMobileItem {
  name: string;
  mobile: string;
  avatarMediaId: number;
  avatarMediaUrl: string;
}

export interface CFMobile {
  code: FeatureType.mobile;
  title: string;
  mobiles: CFMobileItem[];
}

export interface CFMap {
  code: FeatureType.map;
  title: string;
  location: MapLocation;
}

export interface CFMediaImage {
  mediaId: number;
  mediaUrl: string;
  content: string;
  contentAlign: 'left' | 'center' | 'right' | 'justify';
}

export enum CFMediaLayoutStyle {
  list = 0,
  waterfall = 1,
  bigPic = 2,
  splitScreen = 3,
}

export interface CFMedia {
  code: FeatureType.media;
  title: string;
  imgs: CFMediaImage[];
  type?: MediaType; // v5.2.3新增，以前皆为素材库类型 1
  musicMediaId: number;
  musicMediaUrl: string;
  musicMediaName: string;
  musicVolume: number;
  musicLoop: BooleanFlag; // 1
  turnOffBgMusicOnPlay: BooleanFlag; // 1
  turnOffBgVoiceOnPlay: BooleanFlag; // 1
  linkTitle: string;
  linkUrl: string;
  openMethod: LinkTarget;
  // v5.3.6 排版方式, 0:列表模式 1:瀑布流模式 2:大图模式 默认0
  layoutStyle?: CFMediaLayoutStyle;
  // v5.3.6 播放时暂停导览讲解, 0:不关闭 1:关闭 默认0
  turnOffRotateOnPlay?: BooleanFlag;
  modalSizeType?: ModalSizeType;
  size?: ModalSize;
}

export interface CFText {
  code: FeatureType.text;
  title: string;
  content: string;
  linkTitle: string;
  linkUrl: string;
  openMethod: LinkTarget;
  modalSizeType?: ModalSizeType;
  size?: ModalSize;
}

export interface CFArticle {
  code: FeatureType.article;
  title: string;
  articles: number[];
  turnOffBgMusicOnPlay: BooleanFlag; // 1
  turnOffBgVoiceOnPlay: BooleanFlag; // 1
  modalSizeType?: ModalSizeType;
  size?: ModalSize;
  openStyle?: OpenStyle;
}

export interface CFAudio {
  code: FeatureType.audio;
  title: string;
  type?: MediaType; // v5.2.3新增，以前皆为素材库类型 1
  musicMediaId: number;
  musicMediaUrl: string;
  musicMediaName: string;
  musicVolume: number;
  musicLoop: BooleanFlag; // 1
  turnOffBgMusicOnPlay: BooleanFlag; // 1
  turnOffBgVoiceOnPlay: BooleanFlag; // 1
  // v5.3.9 播放时暂停导览讲解, 0:不关闭 1:关闭 默认0
  turnOffRotateOnPlay?: BooleanFlag;
}

export interface CFMediaVideo {
  mediaId: number;
  mediaUrl: string;
  mediaName: string;
  coverMediaUrl: string;
}

export interface CFVideo {
  code: FeatureType.video;
  title: string;
  videos: CFMediaVideo[];
  iframeVideo: string;
  turnOffBgMusicOnPlay: BooleanFlag; // 1
  turnOffBgVoiceOnPlay: BooleanFlag; // 1
  linkTitle: string;
  linkUrl: string;
  openMethod: LinkTarget;
  // v5.3.6 排版方式, 0:列表模式 1:瀑布流模式 2:大图模式 默认0
  layoutStyle?: CFMediaLayoutStyle;
  modalSizeType?: ModalSizeType;
  size?: ModalSize;
}

export enum CFViewerPlayType {
  cick = 0,
  auto = 1,
  loop = 2,
}

export interface CFSeqFrame {
  code: FeatureType.seqFrame;
  title: string;
  imgs: {
    mediaId?: number;
    mediaPath?: string;
    mediaFrame?: number;
    mediaSuffix?: string;
  };
  content: string;
  playMethod: CFViewerPlayType; // 2
  playSpeed: number; // 24
  linkTitle: string;
  linkUrl: string;
  openMethod: LinkTarget;
  modalSizeType?: ModalSizeType;
  size?: ModalSize;
}

export type CFSceneSwitchType = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export type CFSceneView = Partial<{ ath: number; atv: number; fov: number }>;

/**
 * extend by CFSceneNext, CFScenePrev
 */
export interface CFScene {
  code: FeatureType.scene;
  title: string;
  sceneId: GeneralID;
  animation?: CFSceneSwitchType;
  /** 离场 */
  exitTargetView?: {
    /** 0: 无动作, 1: 移动到热点, 2: 移动到视角 */
    type: 0 | 1 | 2;
  } & CFSceneView;
  /** 进场 */
  enterInitView?: {
    /** 0: 场景初始视角, 1: 保持视角, 2: 自定义视角 */
    type: 0 | 1 | 2;
  } & CFSceneView;
  /** 进场移动到 */
  enterTargetView?: {
    /** 0: 未设置, 1: 自定义视角 */
    type: 0 | 1;
  } & CFSceneView;
}

export interface CFNonFunc {
  code: FeatureType.noFunc;
  title: string;
}

export interface CFEvent {
  code: FeatureType.event;
  title: string;
  scriptStr: string;
}

export interface CFMiniProgram {
  code: FeatureType.miniProgram;
  title: string;
  // 微信小程序ID
  id: string;
  // 微信小程序页面路径及参数
  url: string;
}

export interface CFMediaDoc {
  mediaId: number;
  mediaUrl: string;
  mediaName: string;
  mediaThumb: string;
}

export interface CFDoc {
  code: FeatureType.doc;
  title: string;
  docs: CFMediaDoc[];
  modalSizeType?: ModalSizeType;
  size?: ModalSize;
  openStyle?: OpenStyle;
}

export type CFModel3D = {
  code: FeatureType.model3d;
  title: string;
  modelTids: string[];
  // 默认关闭, 0
  turnOffBgMusicOnPlay: BooleanFlag;
  // 3D模型显示方式 0: 纯洁模式 1: 原生模式
  displayStyle: 0 | 1;
};

export interface CFPrevScene extends Omit<CFScene, 'sceneId' | 'code'> {
  code: FeatureType.prevScene;
  /** 跳过缩略图隐藏的场景, 默认: 1 */
  skipThumbHidden: 0 | 1;
  /** 最后一个停止, 默认: 0 */
  stopInLast: 0 | 1;
}

export interface CFNextScene extends Omit<CFPrevScene, 'sceneId' | 'code'> {
  code: FeatureType.nextScene;
}

export type CustomFeature =
  | CFLink
  | CFMobile
  | CFMap
  | CFMedia
  | CFText
  | CFArticle
  | CFAudio
  | CFVideo
  | CFSeqFrame
  | CFScene
  | CFNonFunc
  | CFEvent
  | CFMiniProgram
  | CFDoc
  | CFModel3D
  | CFPrevScene
  | CFNextScene;
